import { Card, Field, Fields, Notice } from "../../../components/ui";
import { ResourceView } from "../../../components/states";
import type { AppConfig } from "../../../config";
import { useResource } from "../../../hooks/useResource";
import {
  LABEEB_TIERS,
  tierDisplayName,
  tierRowsOf,
  type LabeebCatalog,
  type LabeebTenantQuota,
  type LabeebTiers,
} from "../../../api/labeeb";
import { LABEEB_LIMIT_KEYS, limitLabel, limitValue } from "../../../api/labeebLimits";
import { LabeebMissing } from "./labeebMissing";

/**
 * The envelope this merchant's Labeeb tier grants.
 *
 * `GET /labeeb/admin/tiers` holds the limits; the tenant's `tier_id` says
 * which row applies. A tier has exactly two named integers, daily and
 * monthly, and the panel reads them by name — not an open map of limit keys.
 */
export function LabeebEnvelope({
  config,
  quota,
  catalog,
}: {
  config: AppConfig;
  /** The tenant's quota document — the tier comes from here, not the detail. */
  quota: ReturnType<typeof useResource<LabeebTenantQuota>>;
  catalog: ReturnType<typeof useResource<LabeebCatalog>>;
}) {
  const tiers = useResource<LabeebTiers>(config, LABEEB_TIERS);
  const cat = catalog.state.kind === "ready" ? catalog.state.data : null;

  const tierId = quota.state.kind === "ready" ? (quota.state.data?.tier_id ?? null) : null;
  const rows = tiers.state.kind === "ready" ? tierRowsOf(tiers.state.data) : [];
  const row = tierId ? rows.find((r) => r.tier_id === tierId) : undefined;

  return (
    <Card
      title={`Tier envelope${tierId ? ` · ${tierDisplayName(cat, tierId)}` : ""}`}
     
    >
      <ResourceView state={tiers.state} what="the envelope" onRetry={tiers.reload} />

      {/* A business Labeeb has no record of has no envelope. Drawing the
          default tier's here read as that merchant's live setup (DA-3588). */}
      <LabeebMissing state={quota.state} />

      {tiers.state.kind === "ready" && !tierId && quota.state.kind !== "notfound" && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          No explicit tier, so the runtime falls back to the default. The envelope
          it lands on is whichever tier <span className="ref">quota.default_tier_id</span>{" "}
          names.
        </p>
      )}

      {tiers.state.kind === "ready" && tierId && !row && (
        <Notice tone="amber" title="No row for this tier">
          The merchant is on <span className="ref">{tierId}</span>, which the tier
          table does not list. Nothing here is enforced against an envelope that
          does not exist.
        </Notice>
      )}

      {tiers.state.kind === "ready" && row && (
        <>
          {/* Two named integers now, not an open map of limit keys. */}
          <Fields>
            {LABEEB_LIMIT_KEYS.map((k) => (
              <Field key={k} label={limitLabel(k)}>
                <span className="tabular-nums">{limitValue(row, k)}</span>
              </Field>
            ))}
          </Fields>
        </>
      )}
    </Card>
  );
}
